import { useState, FormEvent, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import axios from '../api/client'
import { Button } from '../components/common'
import styles from './LoginPage.module.css'

type Tab = 'login' | 'register'

declare global {
  interface Window {
    Telegram?: {
      Login: {
        init: (options: Record<string, unknown>, callback: (data: Record<string, string | number>) => void) => void
        open: (callback?: (data: Record<string, string | number>) => void) => void
        auth: (options: Record<string, unknown>, callback: (data: Record<string, string | number>) => void) => void
      }
    }
  }
}

export function LoginPage() {
  const { login, register } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const openToken = searchParams.get('open_token')
  const inviteCodeFromLink = searchParams.get('invite_code')
  const [tab, setTab] = useState<Tab>(searchParams.get('tab') === 'register' ? 'register' : 'login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [inviteCode, setInviteCode] = useState(inviteCodeFromLink ?? '')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [clientId, setClientId] = useState<string | null>(null)
  const [tgLoading, setTgLoading] = useState(false)

  useEffect(() => {
    axios.get('/auth/telegram/bot').then(({ data }) => {
      if (data.success && data.clientId) setClientId(String(data.clientId))
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (!clientId) return

    const handleTelegramAuth = async (data: Record<string, string | number>) => {
      if (data.error) { setError(String(data.error)); return }
      if (!data.id_token) { setError('No token received from Telegram'); return }
      setTgLoading(true)
      setError(null)
      try {
        const { data: resp } = await axios.post('/auth/telegram/login', { id_token: data.id_token })
        if (!resp.success) throw new Error(resp.error || 'Telegram login failed')
        localStorage.setItem('stonks_token', resp.token)
        window.location.replace('/releases')
      } catch (err: any) {
        setError(err.response?.data?.error || err.message || 'Telegram login failed')
        setTgLoading(false)
      }
    }

    const script = document.createElement('script')
    script.src = 'https://oauth.telegram.org/js/telegram-login.js'
    script.async = true
    script.onload = () => {
      window.Telegram?.Login.init({ client_id: clientId }, handleTelegramAuth)
    }
    document.head.appendChild(script)

    return () => {
      if (document.head.contains(script)) document.head.removeChild(script)
    }
  }, [clientId])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      if (tab === 'login') {
        await login(email, password)
      } else {
        await register(email, password, inviteCode, openToken ?? undefined)
      }
      navigate('/releases', { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <main className={styles.page}>
      <div className={styles.recordArt} aria-hidden="true" />

      <section className={styles.card} aria-label={tab === 'login' ? 'Sign in' : 'Register'}>
        <div className={styles.logo}>
          <img src="/favicon.svg" className={styles.brandIcon} alt="" />
          <span className={styles.brandName}>
            <span className={styles.brandTen}>ten</span>words
          </span>
        </div>

        <div className={styles.tabs} role="group" aria-label="Account access">
          <button
            type="button"
            className={`${styles.tabBtn} ${tab === 'login' ? styles.tabActive : ''}`}
            aria-pressed={tab === 'login'}
            onClick={() => { setTab('login'); setError(null) }}
          >
            Sign in
          </button>
          <button
            type="button"
            className={`${styles.tabBtn} ${tab === 'register' ? styles.tabActive : ''}`}
            aria-pressed={tab === 'register'}
            onClick={() => { setTab('register'); setError(null) }}
          >
            Register
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              type="text"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              className={styles.input}
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="auth-password">Password</label>
            <input
              id="auth-password"
              type="password"
              autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
              className={styles.input}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          {tab === 'register' && !openToken && !inviteCodeFromLink && (
            <div className={styles.field}>
              <label className={styles.label} htmlFor="auth-invite">Invite code</label>
              <input
                id="auth-invite"
                type="text"
                className={`${styles.input} ${styles.inviteInput}`}
                value={inviteCode}
                onChange={e => setInviteCode(e.target.value.toUpperCase())}
                placeholder="XXXXXXXX"
                required
                maxLength={8}
              />
            </div>
          )}

          {error && (
            <div className={styles.error} role="alert">
              <i className="bi bi-exclamation-circle" aria-hidden="true" />
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="md"
            block
            className={styles.submit}
            disabled={isLoading}
            icon={isLoading ? `bi bi-arrow-repeat ${styles.spinIcon}` : undefined}
          >
            {isLoading
              ? (tab === 'login' ? 'Signing in…' : 'Creating account…')
              : (tab === 'login' ? 'Sign in' : 'Create account')}
            {!isLoading && <span className={styles.submitArrow} aria-hidden="true">↗</span>}
          </Button>
        </form>

        {clientId && tab === 'login' && (
          <>
            <div className={styles.divider}><span>or</span></div>
            {tgLoading ? (
              <div className={styles.tgLoading} role="status">
                <i className={`bi bi-arrow-repeat ${styles.spinIcon}`} aria-hidden="true" />
                Authenticating…
              </div>
            ) : (
              <button type="button" className={styles.tgBtn} onClick={() => window.Telegram?.Login.open()}>
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248-1.97 9.289c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.932z"/></svg>
                Sign in with Telegram
              </button>
            )}
          </>
        )}
      </section>
    </main>
  )
}
